import { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { useSettings } from '../settings';
import { submitContact } from '../services/api';
import PremiumSelect from './PremiumSelect';

function ContactForm({ onOpenLegal }) {
  const { t } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    budget: '$25k - $50k',
    projectType: 'AI ad & campaign generation',
    brief: '',
    website: '',
  });
  const [status, setStatus] = useState('');
  const [success, setSuccess] = useState(false);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
  };

  const update = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(t('contact.transmitting'));

    try {
      await submitContact({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        budget: formData.budget,
        project_type: formData.projectType,
        brief: formData.brief,
        website: formData.website,
      });
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        budget: '$25k - $50k',
        projectType: 'AI ad & campaign generation',
        brief: '',
        website: '',
      });
      setStatus('');
      setPrivacyAcknowledged(false);
    } catch (error) {
      setStatus(error?.message || t('contact.sendError'));
    }
  };

  return (
    <section id="contact-form" className="contact-section" style={{ padding: '40px 0' }}>
      <div 
        className="panel glow-card"
        onMouseMove={handleMouseMove}
        style={{ maxWidth: '800px', margin: '0 auto' }}
      >
        <div className="section-heading text-center" style={{ textAlign: 'center', marginBottom: '36px' }}>
          <p className="eyebrow">{t('contact.eyebrow')}</p>
          <h2>{t('contact.title')}</h2>
          <p>{t('contact.description')}</p>
        </div>

        {success ? (
          <div className="note-box" style={{ borderColor: '#10b981', background: 'rgba(16,185,129,0.05)', color: '#a7f3d0', padding: '30px', borderRadius: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <CheckCircle2 size={40} color="#10b981" />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>{t('contact.successTitle')}</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--muted)', lineHeight: '1.6', maxWidth: '500px' }}>
              {t('contact.successText')}
            </p>
          </div>
        ) : (
          <form className="generator-form" onSubmit={handleSubmit}>
            <label className="contact-honeypot" aria-hidden="true" hidden>
              Website
              <input name="website" value={formData.website} onChange={update} tabIndex="-1" autoComplete="off" />
            </label>
            <div className="form-grid two-columns">
              <label>
                {t('contact.name')}
                <input 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={update} 
                  required 
                  placeholder={t('contact.namePlaceholder')}
                />
              </label>
              <label>
                {t('contact.email')}
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={update} 
                  required 
                  placeholder={t('contact.emailPlaceholder')}
                />
              </label>
            </div>

            <div className="form-grid two-columns">
              <label>
                {t('contact.company')}
                <input 
                  type="text" 
                  name="company" 
                  value={formData.company} 
                  onChange={update} 
                  required 
                  placeholder={t('contact.companyPlaceholder')}
                />
              </label>
              <label>
                {t('contact.budget')}
                <PremiumSelect
                  name="budget"
                  value={formData.budget}
                  onChange={update}
                  ariaLabel={t('contact.budget')}
                  options={['< $25k', '$25k - $50k', '$50k - $100k', '$100k+']}
                />
              </label>
            </div>

            <label>
              {t('contact.interested')}
              <PremiumSelect
                name="projectType"
                value={formData.projectType}
                onChange={update}
                ariaLabel={t('contact.interested')}
                options={[
                  'AI ad & campaign generation',
                  'Brand knowledge base (RAG) setup',
                  'AI image & video creatives',
                  'Copywriting automation',
                  'Enterprise / custom plan',
                ]}
              />
            </label>

            <label>
              {t('contact.brief')}
              <textarea 
                name="brief" 
                value={formData.brief} 
                onChange={update} 
                rows="4" 
                required 
                placeholder={t('contact.briefPlaceholder')}
              />
            </label>

            <label className="contact-privacy-check">
              <input
                type="checkbox"
                checked={privacyAcknowledged}
                onChange={(event) => setPrivacyAcknowledged(event.target.checked)}
                required
              />
              <span>
                {t('contact.privacyBefore')} <button type="button" className="text-link" onClick={() => onOpenLegal?.('privacy')}>{t('app.privacyPolicy')}</button>
                {' '}{t('contact.privacyAfter')}
              </span>
            </label>

            <div className="form-actions">
              <button 
                type="submit" 
                className="primary-button" 
                style={{ width: '100%', padding: '16px' }}
                disabled={status === t('contact.transmitting')}
              >
                <Send size={18} />
                {status === t('contact.transmitting') ? status : t('contact.submit')}
              </button>
            </div>
            {status && status !== t('contact.transmitting') && <p className="contact-submit-error" role="alert">{status}</p>}
          </form>
        )}
      </div>
    </section>
  );
}

export default ContactForm;
